/**
 * QuantityInput is a custom HTMLElement that provides an enhanced input functionality for quantities.
 * It includes buttons for incrementing and decrementing the input value and emits a change event when the value is updated.
 */
class QuantityInput extends window.HTMLElement {
  constructor() {
    super()

    // Store a reference to the input element within this custom element
    this.input = this.querySelector('input')
  }

  connectedCallback() {
    // Create a change event that will be dispatched when the input value changes
    this.changeEvent = new Event('change', { bubbles: true })

    // Bind the button click event to the onButtonClick method
    this.boundOnButtonClick = this.onButtonClick.bind(this)

    // Attach the click event listener to the increment and decrement buttons
    this.querySelectorAll('button').forEach((button) =>
      button.addEventListener('click', this.boundOnButtonClick)
    )
  }

  /**
   * Event handler for the increment and decrement button clicks.
   * It updates the input value based on the button clicked and emits a change event if the value was updated.
   * @param {Event} event - The click event object.
   */
  onButtonClick(event) {
    event.preventDefault()
    const previousValue = this.input.value

    // Increment or decrement the input value based on the clicked button
    event.target.name === 'plus' ? this.input.stepUp() : this.input.stepDown()

    // Dispatch the change event if the input value was updated
    if (previousValue !== this.input.value) {
      this.input.dispatchEvent(this.changeEvent)
    }
  }

  /**
   * Lifecycle method called when the custom element is detached from the DOM.
   * It's responsible for cleanup to avoid potential memory leaks.
   */
  disconnectedCallback() {
    // Detach the click event listener from the increment and decrement buttons
    this.querySelectorAll('button').forEach((button) =>
      button.removeEventListener('click', this.boundOnButtonClick)
    )
  }
}

// Register the custom element 'quantity-input'
window.customElements.define('quantity-input', QuantityInput)
